import React, { useContext, useEffect } from "react";

import deliveryTimeNames, {
    deliveryTimeOptions,
} from "../../../../../../providers/DeliveryTime";
import { DataContext } from "../../../../../../providers/PelletsCheckoutDataProvider";
import { DataContext as CalculatorContext } from "../../../../../../providers/PelletsDataProvider";
import { formatDate } from "../../../../../../utils/DateHelpers";
import { roundNumber } from "../../../../../../utils/NumberHelpers";
import NewSelect from "../../../../../shared/forms/NewSelect";
import RadioButton from "../../../../../shared/forms/RadioButton";
import Headline from "../../../../../shared/NewHeadline";
import DatePicker from "./DatePicker";
import * as styles from "./style.module.scss";
import TimePicker from "./TimePicker";

const DeliveryTime = () => {
    const dataContext = useContext(DataContext);
    const calculatorContext = useContext(CalculatorContext);
    const {
        deliveryTime,
        deliveryTimeType,
        deliveryTimeFrame,
        deliveryTimeWishDate,
    } = dataContext.data;
    const {
        setDeliveryTime,
        setDeliveryTimeType,
        setDeliveryTimeFrame,
        setDeliveryTimeWishDate,
        detailsHasChanged,
    } = dataContext.actions;

    const { additions } = calculatorContext.data;

    /**
     * Returns the number of delivery days.
     */
    const getDeliveryDays = () => {
        if (deliveryTime === "short") {
            return additions.delivery_time_short_days;
        }
        if (deliveryTime === "express48") {
            return 2;
        }
        if (deliveryTime === "express24") {
            return 1;
        }

        return additions.delivery_time_days;
    };

    const deliveryDays = getDeliveryDays();

    useEffect(() => {
        if (
            deliveryTimeType === deliveryTimeOptions.time ||
            deliveryTimeType === deliveryTimeOptions.date
        ) {
            if (deliveryTimeFrame === "") {
                setDeliveryTimeFrame(Object.keys(additions.time_frames)[1]);
            }
            if (
                deliveryTimeType === deliveryTimeOptions.date &&
                deliveryTimeWishDate === ""
            ) {
                setDeliveryTimeWishDate(formatDate(additions.min_wish_date));
            }
        }
    }, [deliveryTimeType]);

    /**
     * Returns the cheapest price for a time frame.
     */
    const getCostForTimeFrame = () => {
        const timeFrameKeys = Object.keys(additions.time_frames);
        let cost = parseFloat(
            additions.time_frames[timeFrameKeys[0]].add.replace(",", ".")
        );
        timeFrameKeys.forEach((timeFrame) => {
            if (
                additions.time_frames[timeFrame].add &&
                parseFloat(
                    additions.time_frames[timeFrame].add.replace(",", ".")
                ) < cost
            ) {
                cost = parseFloat(
                    additions.time_frames[timeFrame].add.replace(",", ".")
                );
            }
        });

        return roundNumber(cost);
    };

    /**
     * When the delivery time changes.
     *
     * @param {*} event
     */
    const onChangeDeliveryTime = (event) => {
        const value = event.target.value;
        // If we choose express, forget the date wishes and time frames.
        if (
            deliveryTimeNames[value] === deliveryTimeNames.express48 ||
            deliveryTimeNames[value] === deliveryTimeNames.express24
        ) {
            setDeliveryTimeType(deliveryTimeOptions.standard);
        }
        setDeliveryTime(event.target.value);
        detailsHasChanged();
    };

    /**
     * When the delivery time type changes.
     *
     * @param {*} value
     */
    const onChangeDeliveryTimeType = (value) => {
        setDeliveryTimeType(value);
        setTimeout(() => {
            detailsHasChanged();
        }, 50);
    };

    /**
     * When the delivery time frame changes.
     * @param {*} value
     */
    const onChangeDeliveryTimeFrame = (value) => {
        setDeliveryTimeFrame(value);
        detailsHasChanged();
    };

    return (
        additions && (
            <React.Fragment>
                <Headline>Liefertermin</Headline>

                <div className="field">
                    <div className="control">
                        <NewSelect
                            value={deliveryTime}
                            onChange={onChangeDeliveryTime}
                            options={Object.keys(additions.delivery_times).map(
                                (key) => {
                                    return {
                                        value: key,
                                        text:
                                            String(
                                                additions.delivery_times[key]
                                                    .name
                                            ) +
                                            String(
                                                additions.delivery_times[key]
                                                    .add !== "0,00"
                                                    ? ` (+${additions.delivery_times[key].add} €)`
                                                    : ""
                                            ),
                                    };
                                }
                            )}
                        />
                    </div>
                </div>
                <div
                    className={styles.radioRow}
                    onClick={() => {
                        onChangeDeliveryTimeType(deliveryTimeOptions.standard);
                    }}
                >
                    <RadioButton
                        className={styles.radio}
                        checked={
                            deliveryTimeType === deliveryTimeOptions.standard
                        }
                    />

                    <div className={styles.radioLabel}>
                        Ganztägig möglich
                        <div className={styles.labelSubline}>
                            (innerhalb {deliveryDays} Werktage, Mo.-Fr., 7-18
                            Uhr)
                        </div>
                    </div>
                </div>
                {(deliveryTime === "standard" || deliveryTime === "short") && (
                    <React.Fragment>
                        {Object.keys(additions.time_frames).length > 0 && (
                            <div
                                className={styles.radioRow}
                                onClick={() => {
                                    onChangeDeliveryTimeType(
                                        deliveryTimeOptions.time
                                    );
                                }}
                            >
                                <RadioButton
                                    className={styles.radio}
                                    checked={
                                        deliveryTimeType ===
                                        deliveryTimeOptions.time
                                    }
                                />

                                <div className={styles.radioLabel}>
                                    Wunsch-Uhrzeit ab +{getCostForTimeFrame()} €
                                    <div className={styles.labelSubline}>
                                        (innerhalb {deliveryDays} Werktage,
                                        Mo.-Fr., 7-18 Uhr)
                                    </div>
                                </div>
                            </div>
                        )}
                        {deliveryTimeType === deliveryTimeOptions.time && (
                            <div className={styles.pickerContainer}>
                                <TimePicker
                                    value={deliveryTimeFrame}
                                    onChange={onChangeDeliveryTimeFrame}
                                    withAdditions
                                />
                            </div>
                        )}

                        {additions.wish_date && (
                            <div
                                className={styles.radioRow}
                                onClick={() => {
                                    onChangeDeliveryTimeType(
                                        deliveryTimeOptions.date
                                    );
                                }}
                            >
                                <RadioButton
                                    className={styles.radio}
                                    checked={
                                        deliveryTimeType ===
                                        deliveryTimeOptions.date
                                    }
                                />

                                <div className={styles.radioLabel}>
                                    Wunsch-Datum +{additions.wish_date}&nbsp;€
                                </div>
                            </div>
                        )}

                        {deliveryTimeType === deliveryTimeOptions.date && (
                            <div className={styles.pickerContainer}>
                                <DatePicker
                                    value={deliveryTimeWishDate}
                                    onChange={setDeliveryTimeWishDate}
                                />
                            </div>
                        )}
                    </React.Fragment>
                )}
            </React.Fragment>
        )
    );
};

export default DeliveryTime;
